<template>
  <div
    class="electron-no-drag"
    :class="{
      'sub-header': !mainHeader,
      'add-filter-container': noPills,
    }"
  >
    <v-popover
      ref="filterPopover"
      interactive
      tag="div"
      content-tag="div"
      :role="'poper'"
      :trigger="'click'"
      :append-to="bodyRef"
      @state="onStateChange"
    >
      <template
        v-if="noPills"
        #default
      >
        <v-popover :on-show="() => !clearAllFiltersBtn && !showMenu">
          <ButtonCustom
            :text="buttonLabel"
            :type="'important-custom'"
            :variation="'outlined'"
            :button-dimensions="buttonDimensions"
            :label-left="true"
            :custom-icon-name="iconName"
            :disabled="isNewBoard"
            :no-label="noButtonLabel"
            :custom-icon-size="'12px'"
            :theme-style="true"
            @click="clickHandler"
          />
          <template #content="{ state }">
            <TooltipTemplate
              v-if="state.isVisible"
              :tooltip-text="buttonLabel"
              :tooltip-meta="{ icon: { ctrl: false, key: 'F' } }"
            />
          </template>
        </v-popover>
      </template>
      <template #content="{ hide }">
        <FilterPopup
          v-if="showPopup"
          :main-header="mainHeader"
          @hide-filter-popup="hide"
        />
      </template>
    </v-popover>
  </div>
</template>

<script>
import ButtonCustom from '@/components/elements/ButtonCustom';
import FilterPopup from '@/components/widgets/filters/FilterPopup';
import TooltipTemplate from '@/components/elements/TooltipTemplate';
import EventBus from '@/utilities/eventBus';
import viewFiltersMixin from '@/mixins/viewFiltersMixin';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';

export default {
  name: 'AddFilter',

  components: {
    ButtonCustom,
    FilterPopup,
    TooltipTemplate,
  },

  mixins: [viewFiltersMixin],

  props: {
    mainHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    noButtonLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    noPills: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  emits: ['popup-change'],

  setup() {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      bodyRef: document.body,
      emptyFilters: { include: {}, exclude: {} },
    };
  },

  data() {
    return {
      showPopup: false,
      clearAllFiltersBtn: false,
      listenersAdded: false,
      showMenu: false,
    };
  },

  computed: {
    isAnyCardDisplayed() {
      return this.$store.getters.getIsAnyCardDisplayed;
    },
    isNewBoard() {
      return this.$route.name === 'newboard';
    },

    buttonDimensions() {
      return {
        width: this.mainHeader ? 'max-content' : 24,
        height: this.mainHeader ? 24 : 22,
      };
    },

    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.boardId;
      }
      return this.$store.getters.currentBoardId;
    },

    currentPills() {
      if (this.isViewOpen) {
        return this.$store.getters.getViewPills;
      }

      if (!this.currentBoardId) return [];
      return this.$store.getters.boardsPills(this.currentBoardId) || [];
    },

    buttonLabel() {
      if (this.clearAllFiltersBtn) return this.translate('clearAllFilters');
      return this.translate('filter');
    },

    iconName() {
      if (this.clearAllFiltersBtn) return 'x';
      return 'plus';
    },

    anyOtherPopupIsOn() {
      return (
        this.$store.getters.getQuickPage ||
        this.$store.getters.getQuickCardIsOn ||
        this.$store.getters.showSearchModal
      );
    },
  },

  watch: {
    showMenu(newValue) {
      if (!newValue && this.mainHeader) {
        // filter menu closed
        if (this.currentPills.length > 0) {
          this.clearAllFiltersBtn = true;
        }
      }
    },
    currentPills() {
      if (this.currentPills.length === 0) {
        this.clearAllFiltersBtn = false;
      }
      if (this.currentPills.length > 0 && this.mainHeader && !this.showMenu) {
        this.clearAllFiltersBtn = true;
      }
    },
  },

  mounted() {
    this.clearAllFiltersBtn = this.mainHeader && this.currentPills.length > 0;
    this.addEventListeners();
    EventBus.$on('openFilterMenu', this.openFilterMenu);
  },

  activated() {
    this.addEventListeners();
  },

  deactivated() {
    this.removeEventListeners();
  },

  beforeUnmount() {
    EventBus.$off('openFilterMenu', this.openFilterMenu);
    this.removeEventListeners();
  },

  methods: {
    clickHandler(e) {
      if (this.clearAllFiltersBtn) {
        if (this.isViewOpen) {
          this.setViewFilters(this.emptyFilters);
          return;
        }

        this.$store.dispatch('setBoardFilters', {
          boardId: this.currentBoardId,
          filters: this.emptyFilters,
        });
        e.stopPropagation();
        e.preventDefault();
      }
    },

    openFilterMenu() {
      this.$refs.filterPopover?.show?.();
    },

    async onStateChange(state) {
      this.showMenu = state.isVisible;
      if (!state.isVisible) {
        // leave some time for the popup to gracefully destroy vuescroll
        this.hideTimeout = setTimeout(() => {
          this.showPopup = false;
        }, 100);
      } else {
        if (this.hideTimeout) {
          // immediately hide the popup if it's still open
          clearTimeout(this.hideTimeout);
          this.showPopup = false;
          await this.$nextTick();
        }
        this.showPopup = true;
      }
      this.$emit('popup-change', state.isVisible);
    },

    keydownHandler(event) {
      if (this.$store.getters.getCurrentCardIdMemberDropdown) return;
      const { tagName, isContentEditable } = document.activeElement;
      if (
        tagName.toLowerCase() === 'input' ||
        tagName.toLowerCase() === 'textarea' ||
        isContentEditable ||
        this.anyOtherPopupIsOn
      ) {
        return;
      }

      if (event.code === 'KeyF' || event.key === 'F' || event.key === 'f') {
        if (event.metaKey || event.ctrlKey) {
          return;
        }
        if (
          (!this.clearAllFiltersBtn || (this.currentPills.length && !this.mainHeader)) &&
          !this.isNewBoard &&
          !this.isAnyCardDisplayed
        ) {
          this.openFilterMenu();
        }
      }
    },

    addEventListeners() {
      if (this.listenersAdded) return;
      window.addEventListener('keydown', this.keydownHandler);
      this.listenersAdded = true;
    },

    removeEventListeners() {
      if (!this.listenersAdded) return;
      window.removeEventListener('keydown', this.keydownHandler);
      this.listenersAdded = false;
    },
  },
};
</script>

<style lang="scss">
.add-filter-container {
  margin-left: 8px;

  &.sub-header {
    margin-left: 0px;

    .button-wrap {
      padding: 0;

      &.outlined {
        box-shadow: none;
        @apply bg-important-bg;

        .button-icon {
          margin-left: 0;
        }

        &:hover {
          @apply bg-important-hover;
        }
      }
    }
  }

  .button-wrap {
    &.outlined {
      box-shadow: none;
      @apply hover:bg-important-hover bg-important-bg;
      &.disabled {
        background-color: transparent;
        border-radius: 12px;
        .button-label {
          @apply border-inactive-border text-inactive-text;
        }
      }
    }
    .button-label {
      font-weight: 500;
      @apply text-text-default;
    }
    em {
      background-color: var(--iconDefault) !important;
    }
  }

  .v-popover {
    display: flex;
  }
}
</style>
